/**
 * Dexie adapter — IndexedDB persistence with rolling backup rotation.
 *
 * # Responsibilities
 *
 * 1. Own the Dexie database (two object stores: `meta` and `saves`)
 * 2. Construct the install secret source (Layer 2's IndexedDBInstallSecret)
 * 3. Write saves with atomic rotation (current → backup1 → backup2 → evict)
 * 4. Load latest valid save by cascading through current → backup1 → backup2
 * 5. Surface load failures as a discriminated union (corrupt / cross-install /
 *    version-too-new / not-found) so the controller can route on type
 *
 * # What this adapter does NOT do
 *
 * - Decide WHEN to save (debouncing is the controller's job — Layer 4)
 * - Decide WHAT to do on load failure (UI surface is the controller's job)
 * - Manage the gameStore subscription (gameStore wiring is Layer 5)
 * - Talk to a server (Stage 1+ adds a sync layer above this)
 *
 * # Why rolling 3 backups (Decision 1 of Phase B)
 *
 * Every successful save shifts the prior current → backup1, prior backup1
 * → backup2, prior backup2 evicted. Three generations is the empirical
 * sweet spot used by Stardew, Terraria, RimWorld — enough recovery depth
 * without unbounded disk growth.
 *
 * # Why rotation BEFORE write (atomicity model)
 *
 * Rotation and the new write happen in a single Dexie 'rw' transaction.
 * If anything in the transaction throws, IndexedDB rolls back atomically.
 * No torn state.
 *
 * # Why save slots are numeric integers (0/1/2)
 *
 * Slot 0 = current, slot 1 = backup1, slot 2 = backup2. Numeric primary
 * keys are slightly faster than string primary keys in IndexedDB and the
 * rotation loop becomes a simple `for (let i = MAX-1; i > 0; i--)`.
 *
 * # Why injection (HmacProvider)
 *
 * The adapter is testable in isolation: tests inject test doubles or the
 * Node provider; production injects the browser provider. Aligns with
 * v2.7's cross-runtime principle.
 *
 * @see {@link ./browserHmacProvider.ts} — browser HMAC provider
 * @see {@link ./installSecret.ts} — install secret persistence
 * @see {@link "@aevons-wake/core/save/integrity"} — verifyChecksum used during load
 */

import Dexie, { type Table } from 'dexie';
import {
  attachChecksum,
  buildEnvelopeForChecksum,
  deserialize,
  envelopeToJson,
  IntegrityError,
  migrate,
  SaveTooNewError,
  verifyChecksum,
  type HmacProvider,
  type LocalState,
  type SaveEnvelope,
} from '@aevons-wake/core';
import {
  IndexedDBInstallSecret,
  type MetaRow,
} from './installSecret';

/** Number of save slots maintained (current + 2 backups). Decision 1 of Phase B. */
export const SAVE_SLOT_COUNT = 3;

/** Slot indices. 0 = current (newest), 1 = backup1, 2 = backup2 (oldest). */
export const SAVE_SLOT_CURRENT = 0;

/** Default Dexie database name. Override via constructor for tests. */
export const DEFAULT_DB_NAME = 'aevons-wake';

/** Shape of a row in the `saves` Dexie table. */
export interface SaveRow {
  /** Slot number (0 = current, 1 = backup1, 2 = backup2). */
  slot: number;
  /** Serialized SaveEnvelope JSON. */
  json: string;
  /** Unix ms when this row was written. For diagnostics only. */
  writtenAt: number;
}

/**
 * Discriminated union of load outcomes.
 *
 * The controller (Layer 4) routes on the `kind` discriminant to drive UI:
 *
 * - `loaded` → state is valid, replace gameStore state
 * - `loaded-from-backup` → cascaded past current; show banner
 * - `not-found` → first launch (or wiped); start fresh
 * - `corrupt-all` → all 3 slots failed; start fresh + warn (Decision 3)
 * - `cross-install` → HMAC verifies with wrong install secret; modal (Decision 3)
 * - `version-too-new` → save is from a newer game version; modal (Decision 3)
 */
export type LoadResult =
  | { kind: 'loaded'; envelope: SaveEnvelope; slotUsed: number }
  | { kind: 'loaded-from-backup'; envelope: SaveEnvelope; slotUsed: number }
  | { kind: 'not-found' }
  | { kind: 'corrupt-all'; lastError: Error }
  | { kind: 'cross-install'; lastError: Error }
  | { kind: 'version-too-new'; lastError: SaveTooNewError };

/** Internal database type — typed Dexie subclass. */
type AevonsWakeDB = Dexie & {
  meta: Table<MetaRow, string>;
  saves: Table<SaveRow, number>;
};

/**
 * IndexedDB-backed save adapter with rolling 3-save backups.
 *
 * # Lifecycle
 *
 * 1. Construct (sync, no I/O)
 * 2. Call `open()` — opens database, ensures install secret exists (async)
 * 3. Call `save(state)` and `loadLatest()` freely (both async)
 * 4. Call `close()` on shutdown (optional; browser cleans up on tab close)
 */
export class DexieSaveAdapter {
  private readonly db: AevonsWakeDB;
  private readonly hmacProvider: HmacProvider;
  private readonly installSecret: IndexedDBInstallSecret;
  private opened = false;

  constructor(
    hmacProvider: HmacProvider,
    options: { dbName?: string } = {},
  ) {
    const dbName = options.dbName ?? DEFAULT_DB_NAME;
    const db = new Dexie(dbName) as AevonsWakeDB;
    db.version(1).stores({
      meta: 'key', // primary key: string
      saves: 'slot', // primary key: number (0, 1, 2)
    });
    this.db = db;
    this.hmacProvider = hmacProvider;
    this.installSecret = new IndexedDBInstallSecret(db.meta);
  }

  /**
   * Open the database and ensure the install secret exists. Idempotent.
   */
  async open(): Promise<void> {
    if (this.opened) return;
    await this.db.open();
    await this.installSecret.ensurePersisted();
    this.opened = true;
  }

  /**
   * Close the database. Useful for tests to release the database between cases.
   */
  close(): void {
    if (this.opened) {
      this.db.close();
      this.opened = false;
    }
  }

  /**
   * Persist a new save with atomic backup rotation.
   *
   * Steps (all inside a single 'rw' transaction):
   *   1. Build envelope (sign with installSecret + hmacProvider)
   *   2. Read existing slots 0..MAX-1
   *   3. Shift each existing slot one position older (0 → 1, 1 → 2)
   *      Slot MAX-1 (oldest) is overwritten and effectively evicted
   *   4. Write the new envelope to slot 0
   *
   * If anything in the transaction throws, IndexedDB rolls back atomically.
   *
   * @param state - the LocalState to persist
   * @param installId - the install UUID (from Phase B's install ID generator)
   * @returns the SaveEnvelope that was persisted
   */
  async save(state: LocalState, installId: string): Promise<SaveEnvelope> {
    this.assertOpen();

    // Build + sign envelope OUTSIDE the transaction (pure-functional, no I/O).
    // createdAt is Unix milliseconds (number) per Phase A's schema.
    const createdAt = Date.now();
    const preChecksum = buildEnvelopeForChecksum(state, installId, createdAt);
    const envelope = attachChecksum(
      preChecksum,
      this.installSecret,
      this.hmacProvider,
    );
    const json = envelopeToJson(envelope);

    // Atomic rotation + write.
    await this.db.transaction('rw', this.db.saves, async () => {
      // Read all existing slots into memory (small payload, max 3 rows).
      const existing: Map<number, SaveRow> = new Map();
      for (let i = 0; i < SAVE_SLOT_COUNT; i++) {
        const row = await this.db.saves.get(i);
        if (row) existing.set(i, row);
      }

      // Shift older. Walk from oldest backup slot down to slot 1.
      // Each iteration moves slot (i-1) → slot i.
      for (let i = SAVE_SLOT_COUNT - 1; i > 0; i--) {
        const previous = existing.get(i - 1);
        if (previous) {
          await this.db.saves.put({
            slot: i,
            json: previous.json,
            writtenAt: previous.writtenAt,
          });
        } else {
          // Nothing to shift into this slot; ensure it's empty if it
          // had stale data (defensive, normally a no-op).
          await this.db.saves.delete(i);
        }
      }

      // Write the new envelope to slot 0 (current).
      await this.db.saves.put({
        slot: SAVE_SLOT_CURRENT,
        json,
        writtenAt: createdAt,
      });
    });

    return envelope;
  }

  /**
   * Load the latest valid save, cascading through backups on failure.
   *
   * Returns a LoadResult discriminated union — see the type for kinds.
   * Does NOT throw on missing or corrupt data; instead returns a tagged
   * failure for the controller to route on.
   *
   * Cascade logic:
   *   - Try slot 0. If valid, return as `loaded`.
   *   - SaveTooNewError short-circuits — newer saves don't get easier in
   *     older backups (they were written by the newer build).
   *   - Otherwise (corrupt JSON, schema reject, HMAC mismatch), try slot 1,
   *     then slot 2.
   *   - If all 3 slots fail with HMAC errors uniformly → `cross-install`.
   *   - If all 3 slots fail with mixed errors → `corrupt-all`.
   *   - If all 3 slots are absent → `not-found`.
   */
  async loadLatest(): Promise<LoadResult> {
    this.assertOpen();

    let lastError: Error | null = null;
    let allSlotsAreIntegrityErrors = true;
    let anySlotAttempted = false;

    for (let slot = 0; slot < SAVE_SLOT_COUNT; slot++) {
      const row = await this.db.saves.get(slot);
      if (!row) {
        continue; // Empty slot — keep trying further slots
      }

      anySlotAttempted = true;

      try {
        const envelope = deserialize(row.json);
        const verified = verifyChecksum(
          envelope,
          this.installSecret,
          this.hmacProvider,
        );

        // Run migrations if save predates current SAVE_VERSION.
        // gameState.version is the content version (per Phase A schema).
        // migrate() throws SaveTooNewError if save is FROM THE FUTURE.
        const migrated = migrate(
          verified.state.gameState,
          verified.state.gameState.version,
        ) as LocalState['gameState'];

        const finalEnvelope: SaveEnvelope = {
          ...verified,
          state: {
            ...verified.state,
            gameState: migrated,
          },
        };

        return slot === 0
          ? { kind: 'loaded', envelope: finalEnvelope, slotUsed: slot }
          : {
              kind: 'loaded-from-backup',
              envelope: finalEnvelope,
              slotUsed: slot,
            };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // SaveTooNewError is terminal — short-circuit, don't try backups.
        if (error instanceof SaveTooNewError) {
          return { kind: 'version-too-new', lastError: error };
        }

        // Track whether all errors were integrity (HMAC) failures.
        // If so → cross-install. If mixed → general corruption.
        if (!(error instanceof IntegrityError)) {
          allSlotsAreIntegrityErrors = false;
        }

        continue;
      }
    }

    // Exhausted all slots.
    if (!anySlotAttempted) {
      return { kind: 'not-found' };
    }
    if (lastError !== null && allSlotsAreIntegrityErrors) {
      return { kind: 'cross-install', lastError };
    }
    return { kind: 'corrupt-all', lastError: lastError ?? new Error('unknown') };
  }

  /**
   * Get the install secret source. Useful for tests and for the controller
   * to construct envelopes outside of save() (e.g., for export).
   */
  getInstallSecret(): IndexedDBInstallSecret {
    return this.installSecret;
  }

  /**
   * Get the underlying meta table. The controller uses this to persist
   * the install ID alongside the install secret. Reusing the adapter's
   * Dexie connection avoids parallel-connection contention during teardown.
   */
  getMetaTable(): Table<MetaRow, string> {
    return this.db.meta;
  }

  /**
   * Test/diagnostic utility: read all save rows. Production should not use
   * this — it bypasses the cascade logic.
   */
  async _listAllSlotsForTests(): Promise<SaveRow[]> {
    return this.db.saves.toArray();
  }

  /**
   * Test/diagnostic utility: directly write a row to a specific slot.
   * Used by tests to set up corruption scenarios.
   */
  async _putRawForTests(row: SaveRow): Promise<void> {
    await this.db.saves.put(row);
  }

  private assertOpen(): void {
    if (!this.opened) {
      throw new Error(
        'DexieSaveAdapter used before open(). Call await adapter.open() first.',
      );
    }
  }
}